<script setup lang="ts">
import { ref, computed, watch, type ComponentPublicInstance } from "vue";
import { Modal } from "bootstrap";
import { useAdminAddUserToOrg } from "@/queries/orgs";
import { useSessionStore } from "@/stores/session-store";
import { useAlertStore } from "@/stores/alerts-store";
import { storeToRefs } from "pinia";

const props = defineProps<{
  show: boolean;
  orgId?: string; // Optional: if provided, add user to this org instead of current org
}>();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "userAdded"): void;
}>();

const { adminAddUserToOrg } = useAdminAddUserToOrg();
const sessionStore = useSessionStore();
const alertStore = useAlertStore();
const { session } = storeToRefs(sessionStore);

const modalRef = ref<HTMLElement | null>(null);
const modalInstance = ref<Modal | null>(null);

const formData = ref({
  userId: "",
  firstName: "",
  lastName: "",
  role: "OrgMember",
});

const allRoles = [
  { value: "SuperAdmin", label: "Super Admin" },
  { value: "OrgAdmin", label: "Organization Admin" },
  { value: "OrgMember", label: "Organization Member" },
  { value: "Partner", label: "Partner" },
];

// Filter out SuperAdmin role for non-SuperAdmins
const availableRoles = computed(() => {
  if (session.value?.isSuperAdmin) {
    return allRoles;
  }
  return allRoles.filter(r => r.value !== "SuperAdmin");
});

const isSubmitting = ref(false);
const errorMessage = ref("");

watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      modalInstance.value?.show();
      // Reset form
      formData.value = {
        userId: "",
        firstName: "",
        lastName: "",
        role: "OrgMember",
      };
      errorMessage.value = "";
    } else {
      modalInstance.value?.hide();
    }
  }
);

const handleSubmit = () => {
  if (!formData.value.userId.trim()) {
    errorMessage.value = "User ID or Email is required";
    return;
  }

  errorMessage.value = "";
  isSubmitting.value = true;

  const targetOrgId = props.orgId || session.value.currentOrgId;

  adminAddUserToOrg(
    {
      orgId: targetOrgId,
      userId: formData.value.userId,
      role: formData.value.role,
      firstName: formData.value.firstName,
      lastName: formData.value.lastName,
    },
    {
      onSuccess: () => {
        alertStore.addSuccess("User Added", "User has been successfully added to the organization");
        emit("userAdded");
        closeModal();
      },
      onError: (e: any) => { errorMessage.value = e?.userMessage ?? "Failed to add user"; },
      onSettled: () => { isSubmitting.value = false; },
    }
  );
};

const closeModal = () => {
  emit("update:show", false);
};

const onModalShown = () => {
  // Focus on first input when modal opens
  const firstInput = modalRef.value?.querySelector("input");
  firstInput?.focus();
};

const onModalHidden = () => {
  emit("update:show", false);
};

// Initialize modal
const initModal = (el: Element | ComponentPublicInstance | null) => {
  if (el && el instanceof HTMLElement && !modalInstance.value) {
    modalRef.value = el;
    modalInstance.value = new Modal(el);
    el.addEventListener("shown.bs.modal", onModalShown);
    el.addEventListener("hidden.bs.modal", onModalHidden);
  }
};
</script>

<template>
  <div
    :ref="initModal"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="addUserModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title" id="addUserModalLabel">Add User to Organization</h2>
          <button
            type="button"
            class="btn-close"
            @click="closeModal"
            aria-label="Close"
          ></button>
        </div>
        <form @submit.prevent="handleSubmit">
          <div class="modal-body">
            <div v-if="errorMessage" class="alert alert-danger" role="alert">
              {{ errorMessage }}
            </div>

            <div class="mb-3">
              <label for="userId" class="form-label">Email Address</label>
              <input
                type="email"
                class="form-control"
                id="userId"
                v-model="formData.userId"
                placeholder="user@example.com"
                required
              />
              <div class="form-text">
                Enter the user's email address. If the user doesn't exist, they will be created.
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="firstName" class="form-label">First Name <span class="text-muted">(optional)</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="firstName"
                  v-model="formData.firstName"
                  placeholder="John"
                />
              </div>
              <div class="col-md-6 mb-3">
                <label for="lastName" class="form-label">Last Name <span class="text-muted">(optional)</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="lastName"
                  v-model="formData.lastName"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div class="mb-3">
              <label for="role" class="form-label">Role</label>
              <select
                class="form-select"
                id="role"
                v-model="formData.role"
                required
              >
                <option
                  v-for="role in availableRoles"
                  :key="role.value"
                  :value="role.value"
                >
                  {{ role.label }}
                </option>
              </select>
              <div class="form-text">Select the role for this user in the organization.</div>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="bb-btn-cancel"
              @click="closeModal"
              :disabled="isSubmitting"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="bb-btn-primary-cta"
              :disabled="isSubmitting"
            >
              <span
                v-if="isSubmitting"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ isSubmitting ? "Adding..." : "Add User" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
